/**
 * inviteMember.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import { DateTime } from "luxon";
import QRCode from "qr-code-styling";
import * as React from "react";

import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DateTimeValidationError } from "@mui/x-date-pickers/models";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import MenuIcon from "@mui/icons-material/Menu";
import InfoIcon from "@mui/icons-material/Info";
import { Alert, Autocomplete, Button, Checkbox, FormControl, FormControlLabel, IconButton,
  InputLabel, MenuItem, Select, SelectChangeEvent, TextField, 
  Tooltip} from "@mui/material";

import { Dialog } from "../components/dialog.component";

import { BillingType, EMAIL_REGEX, InviteType, MemberRole } from "../models/auth";
import { InviteRequest, InviteResponse } from "../models/requests";
import { authServiceFactory } from "../services/auth.service";
import { AuthContext } from "../auth/auth.context";
import { AccountNavContext } from "./account.context";
import { Group } from "../models/group";
import { groupServiceFactory } from "../services/group.service";
import { Brand } from "../models/brand";

export function InviteMemberContainer (): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const acctContext = React.useContext<AccountNavContext>(AccountNavContext);
  const compMemberships = acctContext.currentAccount?.remainingFreeMemberships;

  function doInvite(req: InviteRequest): Promise<InviteResponse> {
    return authServiceFactory().sendInvite(req);
  }

  return <div className="invite-member-container">
    <div className="main-section-inner-with-header">
      <header className="group-header-nav attached-left-nav">
        <IconButton className="left-nav-outer-toggle show-groups-btn" size="small"
            onClick={acctContext.toggleLeftNav}>
          <MenuIcon />
        </IconButton>
        <h3>{authContext.membership?.accountname || "a company"}: Invite New Member</h3>
      </header>
      <InviteMemberComponent
        accountID={authContext.membership?.accountID || ""}
        brand={authContext.brand}
        compAvailable={compMemberships}
        onInvite={doInvite} />
    </div>
  </div>;
}

interface DialogProps {
  open: boolean,
  accountID: string,
  brand: Brand,
  compAvailable?: number,
  editBilling?: boolean,
  onClose(): void,
  onInvite(req: InviteRequest): Promise<InviteResponse>
}
export function InviteMemberDialog(props: DialogProps): React.ReactElement<any, any> {
  let trigger: (() => void) | null = null;
  function getTrigger(fn: () => void) {
    trigger = fn;
  }
  function onConfirm() {
    if (trigger) { trigger(); }
  }
  return <Dialog title="Invite Member" loading={false} open={props.open}
      confirmText="Invite"
      onClose={props.onClose} onConfirm={onConfirm}>
    <InviteMemberComponent accountID={props.accountID}
        onInvite={props.onInvite}
        hideSend={true}
        editBilling={props.editBilling}
        brand={props.brand}
        getTrigger={getTrigger} />
  </Dialog>;
}

interface InviteMemberProps {
  accountID: string,
  onInvite(req: InviteRequest): Promise<InviteResponse>,
  hideSend?: boolean,
  brand: Brand,
  compAvailable?: number,
  editBilling?: boolean,
  getTrigger?(trigger: () => void): void
}
export function InviteMemberComponent (props: InviteMemberProps): React.ReactElement<any, any> {
  const isGlyph = props.brand === Brand.GLYPH;

  const [errMsg, setErrMsg] = React.useState<string>("");
  const [inviteType, setInviteType] = React.useState<{name: string, value: string} | undefined>({name: "Individual", value: InviteType.individual});
  const [groupSearchValue, setGroupSearchValue] = React.useState<string>("");
  const [huddleSearchValue, setHuddleSearchValue] = React.useState<string>("");
  const [groups, setGroups] = React.useState<Array<Group>>([]);
  const [huddleGroups, setHuddleGroups] = React.useState<Array<Group>>([]);
  const [groupIDs, setGroupIDs] = React.useState<Array<string>>([]);
  const [huddle, setHuddle] = React.useState<Group | null>(null);
  const [email, setEmail] = React.useState<string>("");
  const [emailErr, setEmailErr] = React.useState<string>("");
  const [sendEmail, setSendEmail] = React.useState<boolean>(false);
  const [campaignName, setCampaignName] = React.useState<string>("");
  const [campaignNameErr, setCampaignNameErr] = React.useState<string>("");
  const [complementary, setComplementary] = React.useState<boolean>(false);
  const [role, setRole] = React.useState<{name: string, value: string} | undefined>({name: "User", value: MemberRole.user});
  const [billingType, setBillingType] = React.useState<BillingType | undefined>();
  const [startDate, setStartDate] = React.useState<DateTime>(DateTime.now().startOf("day"));
  const [endDate, setEndDate] = React.useState<DateTime>(DateTime.now().plus({days: 7}).endOf("day"));
  const [startDateErr, setStartDateErr] = React.useState<string>("");
  const [endDateErr, setEndDateErr] = React.useState<string>("");
  const [inviteID, setInviteID] = React.useState<string>("");
  const [inviteUrl, setInviteUrl] = React.useState<string>("");
  const [copyText, setCopyText] = React.useState<string>("Copy To Clipboard");
  const qrCodeRef = React.useRef(null);
  const linkInputRef = React.useRef<HTMLInputElement>(null);
  const [qrCode, ] = React.useState<QRCode>(new QRCode({
    width: 350,
    height: 350,
    image: isGlyph ? "/img/glyph-icon-180x180.png" : "/img/workspace650.png",
    imageOptions: {
      margin: 20
    },
    backgroundOptions: {
      color: "#FFFFFF00"
    },
    cornersSquareOptions: {
      type: "extra-rounded"
    },
    dotsOptions: {
      color: isGlyph ? "#0070C0" : "#db38a6"
      /*gradient: {
        type: "linear",
        colorStops: [{offset: 0, color: "#f24341"}, {offset: 1, color: "#db38a6"}]
      }*/
    }
  }));

  React.useEffect(() => {
    if (inviteUrl) {
      qrCode.update({data: inviteUrl});
    }
    if (qrCodeRef.current) {
      qrCode.append(qrCodeRef.current);
    }
    if (linkInputRef.current) {
      linkInputRef.current.select();
    }
  }, [inviteUrl]);

  React.useEffect(function () {
    const groupService = groupServiceFactory();
    groupService.searchGroups(props.accountID, "", {withPrivate: true}).then(groups => {
      setGroups(groups);
      setHuddleGroups(groups);
    }, err => {
      console.error("Error fetching groups:", err);
      setErrMsg((err as Error).message);
    });
  }, []);

  React.useEffect(function () {
    const searchVal = groupSearchValue.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
    //const regex = new RegExp(, "i");
    groupServiceFactory().searchGroups(props.accountID, searchVal, {withPrivate: true}).then(groups => {
      setGroups(groups);
    }, err => {
      console.error("Error fetching groups:", err);
      setErrMsg((err as Error).message);
    });
  }, [groupSearchValue]);

  React.useEffect(function () {
    const searchVal = huddleSearchValue.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
    groupServiceFactory().searchGroups(props.accountID, searchVal, {withPrivate: true}).then(groups => {
      setHuddleGroups(groups);
    }, err => {
      console.error("Error fetching groups:", err);
      setErrMsg((err as Error).message);
    });
  }, [huddleSearchValue]);

  const inviteTypes = [
    {name: "Individual", value: InviteType.individual},
    {name: "Mass Onboarding", value: InviteType.mass}
  ];

  const IndividualRoles = [
    {name: "Admin", value: MemberRole.admin},
    {name: "Manager", value: MemberRole.manager},
    {name: "User", value: MemberRole.user},
    {name: "Guest", value: MemberRole.guest}
  ];
  const MassRoles = [
    {name: "User", value: MemberRole.user},
    {name: "Guest", value: MemberRole.guest}
  ];
  const isIndividual = !(inviteType?.value === InviteType.mass);
  const roles = isIndividual ? IndividualRoles : MassRoles;

  const billingTypes = [
    {name: "Default", value: undefined},
    {name: "Free", value: BillingType.free},
  ];

  function changeInviteType(event: SelectChangeEvent<string>) {
    const newType = event.target.value as InviteType;
    setInviteType(inviteTypes.find(r => r.value === newType));
    if (newType === InviteType.mass) {
      setRole(MassRoles[1]);
      setEmail("");
    }
  }

  function changeBillingType(event: SelectChangeEvent<string>) {
    const type = billingTypes.find(t => t.value === event.target.value);
    setBillingType(type?.value);
  }

  function onBlur(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    if (propName === "email") {
      if (!email) {
        setEmailErr("Enter a valid email address");
      } else if (!EMAIL_REGEX.test(email)) {
        setEmailErr("Invalid Email Address");
      } else {
        setEmailErr("");
      }
    } else if (propName === "campaignName") {
      if (!campaignName) {
        setCampaignNameErr("A name helps identify this invite later.");
      } else {
        setCampaignNameErr("");
      }
    } else if (propName === "startDate") {
      if (!startDate || !startDate.isValid) {
        setStartDateErr("Please, set a starting date/time");
      } else if (endDate && endDate < startDate) {
        setStartDateErr("Starting date/time is after the ending date/time");
      } else {
        setStartDateErr("");
      }
    } else if (propName === "endDate") {
      if (!endDate || !endDate.isValid) {
        setEndDateErr("Please, set an ending date/time");
      } else if (startDate && endDate < startDate) {
        setEndDateErr("Ending date/time is before the starting date/time");
      } else {
        setEndDateErr("");
      }
    }
  }
  function onDateError(error: DateTimeValidationError | null, propName: string) {
    if (propName === "startDate") {
      switch (error) {
        case "maxDate":
          setStartDateErr("Starting date/time must be in the past");
          break;
        case null:
          setStartDateErr("");
          break;
        default:
          setStartDateErr("Please, set a starting date/time");
          break;
      }
    } else if (propName === "endDate") {
      switch (error) {
        case "maxDate":
          setEndDateErr("Ending date/time must be in the past");
          break;
        case null:
          setEndDateErr("");
          break;
        default:
          setEndDateErr("Please, set an ending date/time");
          break;
      }
    }
  }

  function reset() {
    setInviteUrl("");
    setInviteID("");
    setEmail("");
    setRole({name: "User", value: MemberRole.user});
    setCopyText("Copy To Clipboard");
    setEmailErr("");
    setErrMsg("");
  }

  function disableSend(): boolean {
    return !inviteType || isIndividual ?
      !email || !role || !EMAIL_REGEX.test(email) :
      !role || !campaignName || startDate > endDate;
  }

  async function doInvite() {
    try {
      if (!role || !inviteType) { return; }
      if (isIndividual && (!email || !EMAIL_REGEX.test(email))) { return; }

      const tokenData = await props.onInvite(isIndividual ?  {
        client_id: secrets.SC_CLIENT_ID,
        invitetype: InviteType.individual,
        email: email,
        role: role.value,
        membertype: "paid",
        billingType: billingType || undefined,
        sendEmail: sendEmail,
        groupIDs: groupIDs,
        huddleGroupID: huddle?._id,
        compIfPossible: complementary
      } : {
        client_id: secrets.SC_CLIENT_ID,
        invitetype: InviteType.mass,
        email: "",
        membertype: "paid",
        billingType: billingType || undefined,
        role: role.value,
        sendEmail: false,
        campaignName: campaignName,
        groupIDs: groupIDs,
        huddleGroupID: huddle?._id,
        validAfter: startDate,
        validBefore: endDate,
        compIfPossible: complementary
      });

      setInviteUrl(tokenData.url);
      setInviteID(tokenData.inviteID);

    } catch (err) {
      console.error("Failed sending invite:", err);
      setErrMsg((err as Error).message);
    }
  }
  if (props.getTrigger) {
    props.getTrigger(doInvite);
  }

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopyText("Copied!")
    } catch (err) {
      console.error("Error copying to clipboard:", err);
      setErrMsg("Couldn't copy to clipboard");
    }
  }

  function selectedGroup(_event: React.SyntheticEvent, values: readonly Group[] | null) {
    if (values) {
      setGroupIDs(values.map(g => g._id));
    } else {
      setGroupIDs([]);
    }
  }

  function selectedHuddle(_event: React.SyntheticEvent, value: Group | null) {
    setHuddle(value);
    if (!value) {
      return;
    }
    if (!groupIDs.includes(value._id)) {
      setGroupIDs([...groupIDs, value._id]);
    }
    if (!groups.find(group => group._id === value._id)) {
      console.log("Adding huddle group to groups");
      setGroups([...groups, value]);
    }
  }

  // <p><strong>{inviteUrl}</strong></p>
  if (inviteUrl) {
    return <div className="invite-form-container">
      <h1>All Set!</h1>
      <div className="text-info-block">
        <p>Here is the authenticated invite link for your new member(s):</p>
        <div className="form-wrapper">
          <div className="form-row-narrow">
            <FormControl fullWidth id="code-display-container" data-invite-id={inviteID}>
              <TextField id="code-display" size="small" type="email" name="tokenlink"
                color="secondary"
                value={inviteUrl} fullWidth variant="outlined" autoFocus={true}
                inputRef={linkInputRef} />
            </FormControl>
          </div>
        </div>
        <p>
          <Button color="primary" variant="contained"
              disableRipple disableTouchRipple
              onClick={copyToClipboard}>{copyText}</Button>
        </p>
        {sendEmail ?
          <p>We've also emailed it to {email}</p> :
          email ?
            <p>Send it to {email} privately!</p> :
            <p>Share it privately!</p>}
      </div>
      <div id="qrcode-viewer" ref={qrCodeRef} />
      <div className="text-info-block button-row">
        <Button color="primary"
            disableRipple disableTouchRipple
            onClick={reset}>Invite Another</Button>
      </div>
    </div>;
  }

  function renderIndividualForm(): React.ReactElement<any, any> {
    return <>
      <div className="form-row-narrow">
        <FormControl fullWidth>
          <TextField id="email-input" size="small" type="email" name="email" required
            label="Email"
            variant="outlined"
            color="secondary"
            value={email} fullWidth
            error={!!emailErr}
            helperText={emailErr}
            onBlur={onBlur}
            onChange={event => setEmail((event.target as HTMLInputElement).value)} />
        </FormControl>
      </div>
      <div className="form-row">
        <FormControlLabel control={
          <Checkbox color="secondary"
              checked={sendEmail}
              disableRipple disableTouchRipple
              onChange={event => setSendEmail(event.target.checked)}
              inputProps={{ "aria-label": "primary checkbox" }}
            />}
          label="Send Link in Email Invitation"
          name="sendEmail" />
      </div>
    </>;
  }

  function renderMassOnboardingForm(): React.ReactElement<any, any> {
    return <>
      <div className="form-row">
        <FormControl fullWidth>
          <TextField id="campaign-input" size="small"
            type="text" name="campaignName" required
            label="Campaign Name"
            color="secondary"
            value={campaignName} fullWidth variant="outlined"
            error={!!campaignNameErr}
            helperText={campaignNameErr}
            onBlur={onBlur}
            onChange={event => setCampaignName((event.target as HTMLInputElement).value)} />
        </FormControl>
      </div>
      <div className="form-row">
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <DateTimePicker
            value={startDate}
            onChange={(newValue) => {
              newValue && setStartDate(newValue);
            }}
            onError={e => onDateError(e, "startDate")}
            slotProps={{
              textField: {
                size: "small",
                helperText: startDateErr,
                error: !!startDateErr,
                onBlur: onBlur
              }
            }}
          />
          <DateTimePicker
            disablePast
            value={endDate}
            onChange={(newValue) => {
              newValue && setEndDate(newValue);
            }}
            onError={e => onDateError(e, "endDate")}
            slotProps={{
              textField: {
                size: "small",
                helperText: endDateErr,
                error: !!endDateErr,
                onBlur: onBlur
              }
            }}
          />
        </LocalizationProvider>
      </div>
    </>;
  }

  return <div className="invite-form-container">
    <form className="form-wrapper">
      { errMsg && <Alert
        variant="filled"
        severity="error" onClick={() => setErrMsg("")}>{errMsg}</Alert> }
      <div className="form-row">
        <FormControl fullWidth>
          <InputLabel color="secondary">Invite Type</InputLabel>
          <Select fullWidth id="invite-type-select" name="invitetype" size="small"
              label="Invite Type"
              color="secondary"
              value={inviteType?.value || ""}
              onBlur={onBlur}
              onChange={changeInviteType}>
            {inviteTypes.map(inviteType =>
                <MenuItem key={inviteType.value} value={inviteType.value}>{inviteType.name}</MenuItem>)}
          </Select>
        </FormControl>
      </div>
      <div className="form-row">
        <FormControl fullWidth>
          <InputLabel color="secondary">Role</InputLabel>
          <Select fullWidth id="role-select" name="role" size="small" color="secondary"
              label="Role"
              value={role?.value || ""}
              onBlur={onBlur}
              onChange={event => setRole(roles.find(r => r.value === event.target.value))}>
            {roles.map(role =>
                <MenuItem key={role.value} value={role.value}
                    disableRipple disableTouchRipple>
                  {role.name}
                </MenuItem>)}
          </Select>
        </FormControl>
      </div>
      {props.editBilling ?  
        <div className="form-row">
          <FormControl fullWidth>
            <InputLabel id={"select-billing"}>Billing Type</InputLabel>
            <Select labelId={"select-billing"} fullWidth id="type-select" name="type" 
                size="small" 
                color="secondary"
                label="Billing Type"
                value={billingType || "Default"}
                onBlur={onBlur}
                onChange={changeBillingType}>
              {billingTypes.map(type =>
                <MenuItem key={type.value || "Default"} value={type.value || "Default"}
                    disableRipple disableTouchRipple>
                  {type.name}
                </MenuItem>)}
            </Select>
          </FormControl>
        </div> : <></>}
      {isIndividual ?
        renderIndividualForm() :
        renderMassOnboardingForm()
      }
      <div className="group-search-container form-row">
        <Autocomplete id="group-search"
          multiple
          fullWidth
          size="small"
          options={groups}
          getOptionLabel={(group: Group) => group.groupname}
          onChange={selectedGroup}

          onInputChange={(_event, newInputValue) => {
            setGroupSearchValue(newInputValue);
          }}
          renderInput={(params) =>
            <TextField variant="outlined" color="secondary" {...params} label="Add To Groups (Optional):" placeholder="Group Name" />} />
      </div>
      <div className="group-search-container form-row-med">
        <Autocomplete id="huddle-search"
          fullWidth
          size="small"
          options={huddleGroups}
          getOptionLabel={(group: Group) => group.groupname}
          onInputChange={(_event, newInputValue) => {
            setHuddleSearchValue(newInputValue);
          }}
          onChange={selectedHuddle}
          renderInput={params =>
            <TextField variant="outlined"
              color="secondary" {...params}
              helperText="Invitees will be directed to start/join a huddle in this group after onboarding."
              label="Huddle Group (Optional):" placeholder="Group Name" />}
        />
      </div>
      {props.compAvailable ?  
        <div> 
          <FormControlLabel control={
            <Checkbox color="secondary"
                checked={complementary}
                onChange={event => setComplementary(event.target.checked)}
                inputProps={{ "aria-label": "primary checkbox" }}
              />}
            label="Allocate Complementary Membership if Available"
            name="complementary" />
          <Tooltip className="info-tooltip" title={
            `Allocates a complementary membership if one is available when the invite is claimed. 
              If none are available, the membership will be created as normal.`} arrow>
            <InfoIcon className="info" />
          </Tooltip>
        </div> : <></>}
      { !props.hideSend &&
        <div className="button-row">
          <Button variant="contained" color="primary"
              disableRipple disableTouchRipple
              disabled={disableSend()} onClick={doInvite}>
            {isIndividual ? "Send" : "Create"}
          </Button>
        </div>}
    </form>
  </div>;
}
